import { Suspense, lazy } from "react";
import { Route, Routes } from "react-router-dom";

import ImpersonationBanner from "./components/ImpersonationBanner";
import RequireRole from "./components/RequireRole";
import ChangePasswordPage from "./routes/ChangePasswordPage";
import HomePage from "./routes/HomePage";
import LoginPage from "./routes/LoginPage";
import NotFound from "./routes/NotFound";
import EsqueciSenhaPage from "./routes/EsqueciSenhaPage";
import RedefinirSenhaPage from "./routes/RedefinirSenhaPage";
import StatusPage from "./routes/StatusPage";

/*
 * Divisão de código por rota.
 *
 * O three.js sozinho responde por dois terços do pacote, e a `@zxing` por boa
 * parte do resto. Sem separá-los, quem abre o painel administrativo no
 * escritório baixa o motor 3D que não vai usar, e quem lê a plaqueta no celular
 * baixa o decodificador de câmera que também não vai usar.
 *
 * O que fica no pacote inicial é só o que toda sessão precisa: login, troca de
 * senha e as telas de erro. Tudo que puxa dependência pesada entra por `lazy`.
 */
const PassportPage = lazy(() => import("./routes/passport/PassportPage"));
const ViewerPage = lazy(() => import("./routes/ViewerPage"));
const ScanPage = lazy(() => import("./routes/ScanPage"));
const PlatePage = lazy(() => import("./routes/plate/PlatePage"));

const AdminLayout = lazy(() => import("./routes/admin/AdminLayout"));
const AdminDashboardPage = lazy(() => import("./routes/admin/DashboardPage"));
const ValvesPage = lazy(() => import("./routes/admin/ValvesPage"));
const ValveNewPage = lazy(() => import("./routes/admin/ValveNewPage"));
const ValveImportPage = lazy(() => import("./routes/admin/ValveImportPage"));
const ValveDetailPage = lazy(() => import("./routes/admin/ValveDetailPage"));
const CatalogPage = lazy(() => import("./routes/admin/CatalogPage"));
const PlateTemplatePage = lazy(
  () => import("./routes/admin/PlateTemplatePage"),
);
const ServiceRequestsPage = lazy(
  () => import("./routes/admin/ServiceRequestsPage"),
);
const TenantUsersPage = lazy(() => import("./routes/admin/UsersPage"));
const SettingsPage = lazy(() => import("./routes/admin/SettingsPage"));

const SuperadminLayout = lazy(
  () => import("./routes/superadmin/SuperadminLayout"),
);
const DashboardPage = lazy(() => import("./routes/superadmin/DashboardPage"));
const TenantsPage = lazy(() => import("./routes/superadmin/TenantsPage"));
const NewTenantPage = lazy(() => import("./routes/superadmin/NewTenantPage"));
const UsersPage = lazy(() => import("./routes/superadmin/UsersPage"));
const AuditPage = lazy(() => import("./routes/superadmin/AuditPage"));

/** Enquanto o pedaço da rota chega. Discreto: em rede boa nem aparece. */
function Carregando() {
  return (
    <div className="shell">
      <p className="muted">Carregando…</p>
    </div>
  );
}

/**
 * Mapa de rotas.
 *
 * Três áreas: a pública que a plaqueta abre (`/p/:token`, `/scan`), o painel da
 * empresa (`/painel/*`) e o da plataforma (`/superadmin/*`). O painel do tenant
 * não leva o slug na URL — a sessão já diz de qual empresa ele é, e um slug na
 * barra de endereço só convidaria a trocá-lo.
 */
export default function App() {
  return (
    <>
      {/* Fora das rotas: a faixa precisa acompanhar a sessão personificada em
          qualquer tela, não só na área de superadmin. */}
      <ImpersonationBanner />

      <Suspense fallback={<Carregando />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/entrar" element={<LoginPage />} />
          <Route path="/trocar-senha" element={<ChangePasswordPage />} />

          {/* Recuperação de senha. O endereço de `/redefinir-senha` é o mesmo
              que o backend monta no e-mail — mudar um quebra o outro. */}
          <Route path="/esqueci-senha" element={<EsqueciSenhaPage />} />
          <Route path="/redefinir-senha" element={<RedefinirSenhaPage />} />
          <Route path="/status" element={<StatusPage />} />

          {/* Leitor de câmera. Pública: quem está no chão de fábrica com a
              plaqueta na mão não precisa de conta para abrir o passaporte. */}
          <Route path="/scan" element={<ScanPage />} />

          {/* A rota que a plaqueta abre. Pública quando a empresa assim
              configurar — sem porta de login para navegar no 3D. */}
          <Route path="/p/:token" element={<PassportPage />} />

          {/* Plaqueta física: prévia, geração dos arquivos e gravação da tag.
              Rota de layout **sem caminho**: a tela ganha a barra e o menu do
              painel sem que o endereço mude. Ela é tela de trabalho — ficar fora
              da casca deixava quem entrava sem navegação nenhuma. */}
          <Route
            element={
              <RequireRole papeis={["TENANT_ADMIN", "TENANT_EDITOR"]}>
                <AdminLayout />
              </RequireRole>
            }
          >
            <Route path="/valvulas/:valveId/plaqueta" element={<PlatePage />} />
          </Route>

          {/* Pré-visualização do 3D com dados reais — a mesma cena da rota
              pública, com o toolbar completo. */}
          <Route
            path="/visualizador/:valveId"
            element={
              <RequireRole
                papeis={["TENANT_ADMIN", "TENANT_EDITOR", "TENANT_VIEWER"]}
              >
                <ViewerPage />
              </RequireRole>
            }
          />

          {/* Painel da empresa. Leitura para qualquer papel do tenant; as
              escritas são barradas rota a rota, no backend. */}
          <Route
            path="/painel"
            element={
              <RequireRole
                papeis={["TENANT_ADMIN", "TENANT_EDITOR", "TENANT_VIEWER"]}
              >
                <AdminLayout />
              </RequireRole>
            }
          >
            <Route index element={<AdminDashboardPage />} />
            <Route path="valvulas" element={<ValvesPage />} />
            <Route path="valvulas/nova" element={<ValveNewPage />} />
            <Route path="valvulas/importar" element={<ValveImportPage />} />
            <Route path="valvulas/:valveId" element={<ValveDetailPage />} />
            <Route path="plaqueta/molde" element={<PlateTemplatePage />} />
            <Route path="catalogo" element={<CatalogPage />} />
            <Route path="chamados" element={<ServiceRequestsPage />} />
            <Route path="usuarios" element={<TenantUsersPage />} />
            <Route path="configuracoes" element={<SettingsPage />} />
          </Route>

          <Route
            path="/superadmin"
            element={
              <RequireRole papeis={["SUPERADMIN"]}>
                <SuperadminLayout />
              </RequireRole>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="empresas" element={<TenantsPage />} />
            <Route path="empresas/nova" element={<NewTenantPage />} />
            <Route path="usuarios" element={<UsersPage />} />
            <Route path="auditoria" element={<AuditPage />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}
